/**
 * Splitting a search range into the windows a source will actually accept.
 *
 * A metered source caps how much time one query may cover — FR24's flight
 * summary at 14 days — and rejects the whole request rather than truncating
 * it. A pilot asking "what did I fly last
 * winter?" is asking one question, so the adapter splits it, and the cost
 * estimate counts the windows rather than pretending it was one call.
 */

export interface TimeWindow {
  from: Date;
  to: Date;
}

const DAY_MS = 86_400_000;

/**
 * `[from, to]` split into consecutive windows of at most `maxDays`.
 *
 * @throws RangeError if the range runs backwards — a caller that swapped its
 *   arguments should hear about it rather than silently search nothing.
 */
export function splitRange(from: Date, to: Date, maxDays: number): TimeWindow[] {
  const start = from.getTime();
  const end = to.getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end)) {
    throw new RangeError('A search range needs two real dates.');
  }
  if (end < start) {
    throw new RangeError('A search range must end after it starts.');
  }

  const span = maxDays * DAY_MS;
  const windows: TimeWindow[] = [];
  for (let cursor = start; cursor <= end; cursor += span) {
    windows.push({ from: new Date(cursor), to: new Date(Math.min(cursor + span, end)) });
    // A range that fits in one window still produces exactly one.
    if (cursor + span >= end) {
      break;
    }
  }
  return windows;
}

/** Whole days a range spans, rounded up — what a per-day-priced source bills on. */
export function daysSpanned(from: Date, to: Date): number {
  const span = to.getTime() - from.getTime();
  if (!Number.isFinite(span) || span <= 0) {
    return 1;
  }
  return Math.max(1, Math.ceil(span / DAY_MS));
}

/** Every UTC calendar day a range touches, as `YYYY-MM-DD`. */
export function daysInRange(from: Date, to: Date, limit = 400): string[] {
  const days: string[] = [];
  const cursor = new Date(
    Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate(), 0, 0, 0, 0),
  );
  const end = to.getTime();
  while (cursor.getTime() <= end && days.length < limit) {
    days.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return days;
}

export { DAY_MS };
